import { useState, useEffect } from "react";
import { StoreLayout } from "../../components/StoreLayout";
import { api, apiError, fmtINR } from "../../lib/api";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CheckCircle, Wrench, ShieldCheck, CalendarBlank, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const dayLabel = (isoStr) => new Date(isoStr + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function BookService() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [slotCfg, setSlotCfg] = useState({ slots: [], dates: [], min_date: "", max_date: "", availability: {} });
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", pincode: params.get("pincode") || "", city: "", state: "", issue: "",
    product_category: params.get("category") || "", product_type: params.get("type") || "",
    service_type: params.get("service") || "", amount: Number(params.get("amount") || 0),
  });
  const [dateIdx, setDateIdx] = useState(0);
  const paymentMode = params.get("pay") || "full";
  const bookingAmt = Number(params.get("booking") || 0);
  const upfront = paymentMode === "after" ? 0 : (paymentMode === "partial" ? bookingAmt : form.amount);
  const balance = paymentMode === "after" ? form.amount : (paymentMode === "partial" ? Math.max(0, form.amount - bookingAmt) : 0);
  const [slot, setSlot] = useState("");
  const [agree, setAgree] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const selectedDate = (slotCfg.dates || [])[dateIdx] || slotCfg.min_date || "";

  useEffect(() => {
    const p = {};
    if (form.city) p.city = form.city;
    if (selectedDate) p.date = selectedDate;
    api.get("/public/booking-slots", { params: p }).then((r) => setSlotCfg(r.data)).catch(() => {});
  }, [form.city, selectedDate]);

  useEffect(() => { setSlot(""); }, [dateIdx]);
  useEffect(() => { const p = params.get("pincode"); if (p && /^\d{6}$/.test(p)) lookupPin(p); /* eslint-disable-next-line */ }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupPin = async (pin) => {
    set("pincode", pin);
    if (!/^\d{6}$/.test(pin)) return;
    setPinLoading(true);
    try { const { data } = await api.get(`/public/pincode/${pin}`); setForm((f) => ({ ...f, pincode: pin, city: data.city, state: data.state })); }
    catch (e) {} finally { setPinLoading(false); }
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) { toast.error("Name, email and phone are required"); return; }
    if (!form.address || !/^\d{6}$/.test(form.pincode)) { toast.error("Address and a valid 6-digit pincode are required"); return; }
    if (!form.issue.trim()) { toast.error("Please describe the issue"); return; }
    if (!slot) { toast.error("Please select a time slot"); return; }
    if (!agree) { toast.error("Please accept the Terms & Conditions to continue"); return; }
    setBusy(true);
    try {
      const body = { ...form, scheduled_date: selectedDate, slot, accept_terms: true };
      const { data: order } = await api.post("/payments/guest/create-order", body);
      if (order.mode === "after") {
        setResult(order);  // booking already created, no payment needed
        return;
      }
      const pay = await openRazorpayCheckout(order, { description: `${form.service_type || "Service"} · ${form.product_category || "Fixigo"}` });
      const { data } = await api.post("/payments/guest/verify", {
        order_ref: order.order_ref,
        razorpay_order_id: pay.razorpay_order_id,
        razorpay_payment_id: pay.razorpay_payment_id,
        razorpay_signature: pay.razorpay_signature,
      });
      setResult(data);
    } catch (e) {
      if (e?.message === "Payment cancelled") toast.error("Payment cancelled");
      else toast.error(apiError(e.response?.data?.detail) || e.message);
    }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <StoreLayout>
        <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="booking-confirmed">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto"><CheckCircle size={44} weight="fill" className="text-green-600" /></div>
          <h1 className="font-[Outfit] text-3xl font-extrabold tracking-tight mt-6">Booking Confirmed!</h1>
          <p className="text-[#475569] mt-3">
            {result.partner_assigned
              ? <>Your professional <strong>{result.partner_name}</strong> has been assigned. We've emailed your booking details.</>
              : <>We're matching you with the best professional in your area and will update you shortly. Confirmation emailed.</>}
          </p>
          <Button className="rounded-xl mt-8 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" onClick={() => navigate("/")}>Back to Home</Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-2xl mx-auto px-5 py-10">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Book a service</p>
          <h1 className="font-[Outfit] text-3xl md:text-4xl font-extrabold tracking-tight mt-2">
            {form.service_type ? `${form.service_type} · ${form.product_category}` : "Book a Service"}
          </h1>
          <p className="text-[#475569] mt-2 flex items-center gap-2"><Wrench size={15} weight="fill" className="text-[#4F46E5]" /> No login needed · {paymentMode === "after" ? "Pay after service" : (paymentMode === "partial" ? "Small booking amount now" : "Secure online payment")}{form.amount ? ` · From ${fmtINR(form.amount)}` : ""}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8">
          {(
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Your Name *"><Input data-testid="bk-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-lg mt-1.5" /></Fld>
                <Fld label="Contact No. *"><Input data-testid="bk-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-lg mt-1.5" /></Fld>
              </div>
              <Fld label="Email *"><Input data-testid="bk-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-lg mt-1.5" /></Fld>
              <Fld label="Service Address *"><Textarea data-testid="bk-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-lg mt-1.5" /></Fld>
              <div className="grid grid-cols-3 gap-3">
                <Fld label="Pincode *">
                  <Input data-testid="bk-pincode" maxLength={6} value={form.pincode} onChange={(e) => lookupPin(e.target.value.replace(/\D/g, ""))} className="rounded-lg mt-1.5" />
                  {pinLoading && <span className="text-[11px] text-[#4F46E5]">Looking up…</span>}
                </Fld>
                <Fld label="City"><Input data-testid="bk-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-lg mt-1.5" /></Fld>
                <Fld label="State"><Input data-testid="bk-state" value={form.state} onChange={(e) => set("state", e.target.value)} className="rounded-lg mt-1.5" /></Fld>
              </div>
              <Fld label="What's the issue? *"><Textarea data-testid="bk-issue" value={form.issue} onChange={(e) => set("issue", e.target.value)} placeholder="Describe the problem you're facing (e.g. AC not cooling, water leakage under sink)" className="rounded-lg mt-1.5" /></Fld>

              <div>
                <Label className="label-caps flex items-center gap-1.5"><CalendarBlank size={14} /> Pick a date</Label>
                <div className="flex gap-2 overflow-x-auto mt-2 pb-1" data-testid="date-picker">
                  {(slotCfg.dates || []).map((d, i) => (
                    <button key={d} type="button" onClick={() => setDateIdx(i)} data-testid={`date-${i}`}
                      className={`shrink-0 px-3.5 py-2 rounded-xl text-sm font-semibold border transition-colors duration-200 ${dateIdx === i ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                      {i === 0 && d === todayIso() ? "Today" : dayLabel(d)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-caps flex items-center gap-1.5"><Clock size={14} /> Pick a time slot</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="slot-grid">
                  {(slotCfg.slots || []).map((s) => {
                    const av = (slotCfg.availability || {})[s];
                    const full = av?.full;
                    return (
                      <button key={s} type="button" disabled={full} onClick={() => setSlot(s)} data-testid={`slot-${s.replace(/[^0-9]/g,"").slice(0,4)}`}
                        className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${slot === s ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                        {s}{full ? " · Full" : (av?.remaining != null ? ` · ${av.remaining} left` : "")}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#94a3b8] mt-2">{slotCfg.min_date ? `Earliest available date: ${dayLabel(slotCfg.min_date)}. Time windows are set by Fixigo.` : "Loading available slots…"}</p>
              </div>

              <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="bk-terms-label">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] accent-[#4F46E5]" data-testid="bk-terms-checkbox" />
                <span className="text-xs text-[#475569] leading-relaxed">
                  I agree to the{" "}
                  <a href="/customer-terms" target="_blank" rel="noreferrer" className="text-[#4F46E5] font-semibold hover:underline" data-testid="bk-terms-link">Customer Terms &amp; Conditions</a>, including the cancellation policy.
                </span>
              </label>
              {form.amount > 0 && (
                <div className={`rounded-xl p-3 text-sm ${paymentMode === "after" ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-[#eef2ff] border border-[#c7d2fe] text-[#3730a3]"}`} data-testid="bk-payment-note">
                  {paymentMode === "after"
                    ? <>💡 <strong>Pay after service</strong> — no payment now. You'll pay <strong>{fmtINR(form.amount)}</strong> once the job is completed.</>
                    : paymentMode === "partial"
                    ? <>🔒 Pay a booking amount of <strong>{fmtINR(upfront)}</strong> now; the balance <strong>{fmtINR(balance)}</strong> is payable after the service.</>
                    : <>🔒 <strong>Secure online payment</strong> — pay <strong>{fmtINR(form.amount)}</strong> now; your booking is confirmed instantly.</>}
                </div>
              )}
              <Button className="rounded-xl w-full h-11 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold gap-2" onClick={submit} disabled={busy || !agree} data-testid="bk-submit">
                <ShieldCheck size={18} weight="bold" /> {busy ? "Processing…" : (paymentMode === "after" ? "Confirm Booking" : `Pay${upfront ? ` ${fmtINR(upfront)}` : ""} & Book`)}
              </Button>
            </div>
          )}
        </div>
      </div>
    </StoreLayout>
  );
}

const Fld = ({ label, children }) => (<div><Label className="label-caps">{label}</Label>{children}</div>);
